import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { Screen } from "@/components/Screen";
import { useI18n } from "@/i18n/I18nProvider";
import { LEGAL_CONTENT, LEGAL_META } from "@/content/legal";

/**
 * LegalPage — shared shell for /legal/{privacy,terms,delete-data,ai-notice}.
 * Renders the content module for the given `docKey` in the current language.
 * No bottom nav; a back link returns the user to Settings.
 */
export function LegalPage({ docKey, testId }) {
  const { t, language } = useI18n();
  const doc = LEGAL_CONTENT[docKey];
  if (!doc) return null;

  const title = doc.title[language] || doc.title.pl;
  const effective = LEGAL_META.effectiveLabel[language] || LEGAL_META.effectiveLabel.pl;
  const footer = LEGAL_META.footer[language] || LEGAL_META.footer.pl;

  return (
    <Screen withBottomNav={false} testId={testId}>
      <Link
        to="/settings"
        data-testid="legal-back-link"
        className="inline-flex items-center gap-1.5 text-sm text-[#CBD5E1] hover:text-white transition-colors duration-200 mb-6 mt-2 rounded-md px-1 py-0.5"
      >
        <ArrowLeft size={16} strokeWidth={1.6} />
        {t("common.backToSettings")}
      </Link>

      <header>
        <p className="text-xs uppercase tracking-[0.18em] text-[#E8D8B8]/70">
          {t("app.tagline")}
        </p>
        <h1
          className="mt-2 text-3xl sm:text-4xl font-medium leading-tight"
          style={{ letterSpacing: "-0.02em" }}
          data-testid={`${testId}-title`}
        >
          {title}
        </h1>
        <p className="mt-2 text-xs text-[#94A3B8]" data-testid={`${testId}-effective`}>
          {effective}
        </p>
      </header>

      <article
        className="mt-8 space-y-6"
        data-testid={`${testId}-content`}
      >
        {doc.sections.map((section, i) => (
          <section
            key={i}
            className={
              i === 0
                ? "pt-2"
                : "pt-6 border-t border-white/[0.06]"
            }
          >
            <h2
              className="text-[15px] sm:text-base font-medium text-[#F8FAFC]"
              style={{ letterSpacing: "-0.01em" }}
            >
              {section.heading[language] || section.heading.pl}
            </h2>
            <p className="mt-2 text-[14px] leading-[1.65] text-[#CBD5E1]">
              {section.body[language] || section.body.pl}
            </p>
          </section>
        ))}
      </article>

      <footer
        className="mt-10 pt-6 border-t border-white/[0.06]"
        data-testid={`${testId}-footer`}
      >
        <p className="text-[11px] text-[#94A3B8] leading-relaxed">{footer}</p>
      </footer>
    </Screen>
  );
}
